/**
 * How each camera and screen is recorded: the person picks a mode, and this works out a level (size and frame
 * rate) for every source, so they can see what they'll get before they record.
 *
 *   maximum  every source at the best its device delivers (up to 4K), nothing stepped down; a warning if that's
 *            more than this computer's encoder can likely keep up with
 *   smart    1080p 30 fps for everything, stepped down only as far as needed to fit this computer: screens' frame
 *            rate first (app content looks fine at 15 fps), then extra cameras, the main camera last
 *   light    small files and little load, for slow computers or little free disk
 *
 * Any source can be set by hand; the rest fit around it. Sources already recording keep their level until the
 * take ends (a file can't change size halfway), and new ones fit around them.
 *
 * The budget comes from the hardware check, which runs three full-HD encodes at once (hardware encoders work on
 * several streams in parallel, so one stream alone undersells them). Load is counted in 720p 30 fps streams; 70%
 * of what was measured is allowed, less the live call's own encoding (each camera or screen sent live, once per
 * person receiving it). The 6-second test then corrects the budget from what actually happened.
 */

export type QualityMode = 'maximum' | 'smart' | 'light';
export type VideoKind = 'camera' | 'screen';

export interface Level {
  width: number;
  height: number;
  fps: number;
}

/** Best first. A source's level is an index into its kind's list. */
export const LEVELS: Record<VideoKind, Level[]> = {
  camera: [
    { width: 3840, height: 2160, fps: 30 },
    { width: 2560, height: 1440, fps: 30 },
    { width: 1920, height: 1080, fps: 60 },
    { width: 1920, height: 1080, fps: 30 },
    { width: 1280, height: 720, fps: 30 },
    { width: 1280, height: 720, fps: 24 },
    { width: 960, height: 540, fps: 15 },
  ],
  screen: [
    { width: 3840, height: 2160, fps: 30 },
    { width: 2560, height: 1440, fps: 30 },
    { width: 1920, height: 1080, fps: 60 },
    { width: 1920, height: 1080, fps: 30 },
    { width: 1920, height: 1080, fps: 15 },
    { width: 1280, height: 720, fps: 30 },
    { width: 1280, height: 720, fps: 15 },
    { width: 1280, height: 720, fps: 10 },
  ],
};
const at = (kind: VideoKind, l: Level) => LEVELS[kind].findIndex((x) => x.width === l.width && x.height === l.height && x.fps === l.fps);
const FULL_HD = 3; // 1080p 30 in both lists
const LIGHT: Record<VideoKind, { main: number; extra: number }> = {
  camera: { main: at('camera', { width: 1280, height: 720, fps: 24 }), extra: at('camera', { width: 960, height: 540, fps: 15 }) },
  screen: { main: at('screen', { width: 1280, height: 720, fps: 10 }), extra: at('screen', { width: 1280, height: 720, fps: 10 }) },
};

export const levelLabel = (l: Level) => `${l.height >= 2160 ? '4K' : `${l.height}p`} · ${l.fps} fps`;

/** Load of one stream, in 720p 30 fps streams. */
export const units = (l: Level) => (l.width * l.height * l.fps) / (1280 * 720 * 30);

/** Bits per second for good H.264 at this level: ~8 Mbit/s for a 1080p 30 camera, ~6 for a 1080p 30 screen. */
export function bitrateFor(kind: VideoKind, l: Level) {
  const bitsPerPixel = (kind === 'camera' ? 0.13 : 0.1) * (l.fps > 30 ? 0.75 : 1);
  return Math.max(1_000_000, Math.round((l.width * l.height * l.fps * bitsPerPixel) / 100_000) * 100_000);
}

/** How many 720p 30 streams this computer can record, before the live call's share. */
export function capacityFor(hw: { encodeUnits?: number | null; encodeFps720: number | null; tier: 'high' | 'standard' | 'light' }) {
  if (hw.encodeUnits && hw.encodeUnits > 0) return Math.max(1, hw.encodeUnits * 0.7);
  // Older profiles, or the parallel check didn't finish: one stream's speed, read cautiously.
  if (hw.encodeFps720 && hw.encodeFps720 > 0) return Math.max(1, (hw.encodeFps720 / 30) * 0.5);
  return hw.tier === 'high' ? 6 : hw.tier === 'standard' ? 3 : 1.5;
}

/** The live call's encoding: each camera or screen sent live, once per person receiving it (call quality ≈ ½ unit). */
export const liveLoadFor = (liveVideos: number, receivers: number) => liveVideos * Math.max(1, receivers) * 0.5;

/**
 * The best level for a device, from its track's capabilities (any orientation): the smallest level whose frame
 * still holds the device's full size, so nothing is scaled down below what it delivers (a 1440×1080 camera
 * records at 1440×1080 within the 1080p frame; an app window at its own size). Devices report their largest size
 * and highest frame rate separately (a camera saying "4K" and "20 fps" may well do 1080p at 30), so the frame
 * rate only rules out the 60 fps levels.
 */
export function bestLevelFor(kind: VideoKind, track: MediaStreamTrack): number {
  const caps = (track.getCapabilities?.() ?? {}) as MediaTrackCapabilities;
  const w = caps.width?.max;
  const h = caps.height?.max;
  const fps = caps.frameRate?.max;
  if (!w || !h) return FULL_HD; // Firefox and others that don't say: assume full HD
  const long = Math.max(w, h);
  const short = Math.min(w, h);
  const levels = LEVELS[kind].map((l, i) => ({ ...l, i })).filter((l) => l.fps <= 30 || !fps || fps >= 50);
  const holding = levels.filter((l) => l.width >= long - 8 && l.height >= short - 8);
  // Bigger than every level: the largest.
  if (!holding.length) return levels[0].i;
  const height = Math.min(...holding.map((l) => l.height));
  return holding.find((l) => l.height === height)!.i;
}

export interface PlanSource {
  id: string;
  kind: VideoKind;
  /** The camera on the call, or the first screen: stepped down last. */
  main: boolean;
  /** Best level the device delivers. */
  best: number;
  /** Set by hand. */
  chosen?: number;
  /** Recording now: stays as it is. */
  locked?: number;
}

export interface PlannedSource {
  index: number;
  level: Level;
  bitrate: number;
  /** Lower than this mode would give it, to fit the computer. */
  steppedDown: boolean;
  how: 'auto' | 'chosen' | 'recording';
}

export interface QualityPlan {
  mode: QualityMode;
  sources: Map<string, PlannedSource>;
  /** Recording load against what's left of the computer's capacity after the live call, in 720p 30 fps streams. */
  load: number;
  capacity: number;
  liveLoad: number;
  verdict: 'comfortable' | 'tight' | 'over';
  /** Video only; see storagePerHour for the whole recording. */
  videoBitsPerSecond: number;
}

/** Which source gives way first in Smart: extra screens, the main screen, extra cameras, the main camera. */
const yieldOrder = (s: PlanSource) => (s.kind === 'screen' ? (s.main ? 1 : 0) : s.main ? 3 : 2);

export function planQuality(mode: QualityMode, sources: PlanSource[], total: number, liveLoad = 0): QualityPlan {
  const capacity = Math.max(0.5, total - liveLoad);
  const index = new Map<string, number>();
  const start = new Map<string, number>();
  for (const s of sources) {
    const auto =
      mode === 'maximum' ? s.best : mode === 'smart' ? Math.max(s.best, FULL_HD) : Math.max(s.best, s.main ? LIGHT[s.kind].main : LIGHT[s.kind].extra);
    start.set(s.id, auto);
    index.set(s.id, s.locked ?? (s.chosen != null ? Math.max(s.best, s.chosen) : auto));
  }
  const load = () => sources.reduce((a, s) => a + units(LEVELS[s.kind][index.get(s.id)!]), 0);
  if (mode === 'smart') {
    const movable = sources.filter((s) => s.locked == null && s.chosen == null);
    while (load() > capacity) {
      // The first in yield order that can still go down; among equals, the heaviest.
      const next = movable
        .filter((s) => index.get(s.id)! < LEVELS[s.kind].length - 1)
        .sort((a, b) => yieldOrder(a) - yieldOrder(b) || units(LEVELS[b.kind][index.get(b.id)!]) - units(LEVELS[a.kind][index.get(a.id)!]))[0];
      if (!next) break;
      index.set(next.id, index.get(next.id)! + 1);
    }
  }
  const used = load();
  const planned = new Map<string, PlannedSource>();
  let bits = 0;
  for (const s of sources) {
    const i = index.get(s.id)!;
    const level = LEVELS[s.kind][i];
    const bitrate = bitrateFor(s.kind, level);
    bits += bitrate;
    planned.set(s.id, {
      index: i,
      level,
      bitrate,
      steppedDown: s.locked == null && s.chosen == null && i > start.get(s.id)!,
      how: s.locked != null ? 'recording' : s.chosen != null ? 'chosen' : 'auto',
    });
  }
  return {
    mode,
    sources: planned,
    load: used,
    capacity,
    liveLoad,
    verdict: used <= capacity * 0.8 ? 'comfortable' : used <= capacity ? 'tight' : 'over',
    videoBitsPerSecond: bits,
  };
}

/** Bytes per hour of recording: the video files plus a WAV per microphone or computer sound. */
export function storagePerHour(plan: QualityPlan, wavs: { channels: number; bits: 16 | 24 }[]) {
  const wavBytes = wavs.reduce((a, w) => a + 48000 * w.channels * (w.bits / 8) * 3600, 0);
  return (plan.videoBitsPerSecond / 8) * 3600 + wavBytes;
}
